import { Link } from "react-router-dom";
import Footer from "@/components/site/Footer";
import BookingSidebar from "@/components/site/BookingSidebar";
import hero from "@/assets/tour-homestay-farm.jpg";

const itinerary = [
  {
    time: "Day 1 · 1 hour",
    title: "Hotel Pick-up & Transfer to Village",
    body:
      "Meet your guide at your hotel in Siem Reap and transfer by private vehicle to the countryside village. Meet your host family and settle into your homestay.",
  },
  {
    time: "Day 1 · 45 min",
    title: "Village Walk & Introduction",
    body:
      "Explore the village surroundings, see traditional wooden stilt houses, and learn about daily life in rural Cambodia.",
  },
  {
    time: "Day 1 · 2 hours",
    title: "Farm Activities with Host Family",
    body:
      "Join your host family in the rice and vegetable fields: plough the ground using traditional methods, plant rice (seasonal), cut grass for cow feed, and learn about local crops and farming techniques.",
  },
  {
    time: "Day 1 · 1.5 hours",
    title: "Cooking with the Khmer Family",
    body:
      "Return to the house and cook dinner together. Learn how to prepare authentic Khmer dishes using fresh local ingredients.",
  },
  {
    time: "Day 1 · Overnight",
    title: "Dinner & Overnight Stay",
    body:
      "Enjoy your homemade meal and relax in a peaceful village atmosphere. Overnight at the homestay.",
  },
  {
    time: "Day 2 · 1 hour",
    title: "Morning Routine & Breakfast",
    body:
      "Wake up early and experience village life fresh air, a local breakfast, and the simple daily routines of a Khmer family.",
  },
  {
    time: "Day 2 · 1 hour",
    title: "Monk Blessing Ceremony",
    body:
      "Visit the local pagoda with your host family for a traditional monk blessing. Learn about Buddhist practices and receive blessings for good luck, health, and safe travels.",
  },
  {
    time: "Day 2 · 1 hour",
    title: "Free Time & Departure",
    body:
      "Spend some free time with the family before saying goodbye. Transfer back to your hotel in Siem Reap.",
  },
];

const includes = [
  "Hotel pick-up & drop-off in air-conditioned vehicle",
  "Professional English-speaking guide",
  "Homestay accommodation (1 night)",
  "Meals: 1 dinner & 1 breakfast",
  "Farm activities (ploughing, planting rice, cutting grass)",
  "Cooking session with Khmer family",
  "Monk blessing ceremony at local pagoda",
  "Bottled water & cold towels",
];

const excludes = [
  "Tips and gratuities",
  "Personal expenses",
  "Travel insurance",
  "Lunch & extra drinks",
];

const essentials = [
  "Casual but respectful clothing (covered shoulders & knees in temples)",
  "Comfortable walking shoes",
  "Sun hat, sunscreen & insect repellent",
  "Light change of clothes for farm work",
];

const TourHomestayFarm = () => {
  return (
    <main className="bg-background">
      <header className="relative bg-jungle text-background overflow-hidden">
        <img
          src={hero}
          alt="Life on the Farm Khmer homestay near Siem Reap — authentic Cambodia countryside experience with French-speaking guide"
          width={1920}
          height={1280}
          className="absolute inset-0 h-full w-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-jungle/70 via-jungle/50 to-jungle/95" />
        <div className="relative z-10">
          <div className="container flex items-center justify-between py-6">
            <Link to="/" className="flex items-center gap-2">
              <span className="font-display text-2xl tracking-wide">Aṅkor</span>
              <span className="text-[10px] uppercase tracking-[0.3em] opacity-70">Sojourns</span>
            </Link>
            <Link to="/tours" className="text-[11px] uppercase tracking-[0.3em] border-b border-background/40 pb-1 hover:border-primary hover:text-primary transition-colors">
              ← All Tours
            </Link>
          </div>
          <div className="container pt-20 pb-32 md:pt-28 md:pb-44 max-w-5xl">
            <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— 2 Days / 1 Night Homestay</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95] text-balance">
              Life Farm Homestay <em className="font-light">with</em> a Khmer Family.
            </h1>
            <p className="mt-10 max-w-2xl text-background/75 font-light text-lg leading-relaxed">
              An authentic countryside homestay near Siem Reap-work the fields, cook traditional Khmer dishes, sleep in a village home and receive a peaceful monk blessing before returning.
            </p>
            <div className="mt-10 flex flex-wrap gap-x-10 gap-y-3 text-[11px] uppercase tracking-[0.3em] text-background/80">
              <span>Duration · 2 Days / 1 Night</span>
              <span>Group · 2–15</span>
              <span>Pickup · 9:00 AM / 2:00 PM</span>
            </div>
          </div>
        </div>
      </header>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— The Idea</p>
          <h2 className="font-display text-4xl leading-[1.05] mb-10 text-balance md:text-xl">
            Beyond the temples — into a village home.
          </h2>
          <div className="space-y-6 text-lg font-light text-muted-foreground leading-relaxed">
            <p>
              Experience authentic rural life in Cambodia with a warm Khmer family. This immersive homestay takes you beyond the temples into the countryside, where you will live like a local working in the fields, cooking traditional food, and sharing meaningful cultural moments, including a peaceful monk blessing ceremony.
            </p>
            <p>
              Day 1 is dedicated to village life and farm activities: meet your host family, walk through the village, and join in real farming tasks such as ploughing, planting rice, and cutting grass for the cows. In the evening, cook traditional Khmer dishes together and enjoy dinner under a peaceful village sky.
            </p>
            <p>
              Day 2 begins with a quiet morning routine and a local breakfast, followed by a visit to the village pagoda for a traditional monk blessing a moment of reflection, gratitude, and good wishes for your onward journey.
            </p>
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_420px] gap-x-12 gap-y-12 items-start">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Itinerary</p>
              <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-16 text-balance">
                Two days, one night, eight stops.
              </h2>
              <ol className="space-y-px">
                {itinerary.map((s, idx) => (
                  <li
                    key={idx}
                    className="group grid grid-cols-[auto_1fr] md:grid-cols-[200px_1fr] gap-x-4 md:gap-x-12 min-w-0 border-t border-border py-8 md:py-10 transition-colors hover:bg-secondary/40 last:border-b"
                  >
                    <div className="flex flex-col items-start gap-y-2 text-base">
                      <span className="font-display text-primary tabular-nums text-xl">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <span className="uppercase tracking-[0.25em] text-primary text-xs md:text-base">
                        {s.time}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-display text-2xl md:text-3xl leading-tight mb-3 transition-colors group-hover:text-primary">
                        {s.title}
                      </h3>
                      <p className="text-muted-foreground font-light leading-relaxed text-lg max-w-2xl">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
            <BookingSidebar pricePerPerson={279} tourTitle="Life Farm Homestay with Khmer Family – 2 Days / 1 Night" pricingTiers={[{ maxPax: 1, price: 279 }, { maxPax: 2, price: 345 }, { maxPax: 3, price: 367 }, { maxPax: 4, price: 397 }, { maxPax: 5, price: 397 }, { maxPax: 6, price: 424 }, { maxPax: 7, price: 467 }, { maxPax: 8, price: 496 }, { maxPax: 9, price: 545 }, { maxPax: 10, price: 589 }]} />
          </div>
        </div>
      </section>

      <section className="bg-secondary py-24 md:py-32">
        <div className="container max-w-5xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— What's Included</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Everything you need for the stay.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
            <div>
              <h3 className="font-display text-2xl mb-5">Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {includes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-5">Not Included</h3>
              <ul className="space-y-3 font-light text-lg text-muted-foreground">
                {excludes.map((it) => (
                  <li key={it} className="border-b border-border pb-3 last:border-b-0">{it}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="container max-w-4xl">
          <p className="text-xs uppercase tracking-[0.4em] text-primary mb-6">— Good to Know</p>
          <h2 className="font-display text-4xl md:text-5xl leading-[1.05] mb-12 text-balance">
            Plan your visit.
          </h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            <div>
              <h3 className="font-display text-2xl mb-4">Essential Items</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                {essentials.map((it) => (
                  <li key={it}>• {it}</li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Weather</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Hot and dry weather is expected throughout this tour. Occasional showers are possible but most days remain dry. Midday temperatures can be very hot, though most countryside sites have low humidity.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Food</h3>
              <p className="font-light text-muted-foreground leading-relaxed">
                Khmer cuisine is similar to most Southeast Asian cooking but rather less spicy. Good bread is widely available thanks to French influence, and there is a surprising range of local brews to try.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl mb-4">Additional Information</h3>
              <ul className="space-y-2 font-light text-muted-foreground">
                <li>• Pickup time confirmed one day before</li>
                <li>• Not wheelchair accessible</li>
                <li>• Leave valuables at your hotel; bring only what you need</li>
                <li>• Children under 8 years old are not permitted</li>
                <li>• Maximum group size: 2–15 travellers in an air-conditioned vehicle</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TourHomestayFarm;
